import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeftRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Merge,
  TriangleAlert,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  FreelancerAutocomplete,
  type FreelancerOption,
} from "@/components/freelancers/FreelancerAutocomplete";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useFreelancersInvalidate } from "@/hooks/use-freelancers-invalidate";
import {
  executeMerge,
  labelForFkKey,
  previewMerge,
  type MergeFreelancerSnapshot,
  type MergePreview,
} from "@/services/freelancer-merge-api";

type Step = 1 | 2 | 3;

interface FreelancerMergeWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Freelancer da rota atual — entra como NEW por padrão. */
  currentFreelancer: { id: string; nome: string; email: string };
}

interface FreelancerLite {
  id: string;
  nome: string;
  email: string;
}

// Campos do perfil exibidos no diff (apenas os relevantes pra decisão de merge)
const DIFF_FIELDS: Array<{ key: keyof MergeFreelancerSnapshot; label: string }> = [
  { key: "nome", label: "Nome" },
  { key: "email", label: "E-mail" },
  { key: "telefone", label: "Telefone" },
  { key: "cidade", label: "Cidade" },
  { key: "pretensao_salarial", label: "Pretensão" },
  { key: "areas", label: "Áreas" },
  { key: "linkedin_url", label: "LinkedIn" },
  { key: "portfolio_url", label: "Portfolio" },
  { key: "created_at", label: "Cadastrado em" },
  { key: "updated_at", label: "Atualizado em" },
];

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    try {
      return new Date(value).toLocaleString("pt-BR");
    } catch {
      return value;
    }
  }
  return String(value);
}

function isDifferent(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) return a.join("|") !== b.join("|");
  return (a ?? null) !== (b ?? null);
}

export function FreelancerMergeWizard({
  open,
  onOpenChange,
  currentFreelancer,
}: FreelancerMergeWizardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const invalidateFreelancers = useFreelancersInvalidate();

  const [step, setStep] = useState<Step>(1);
  // Por padrão, o atual é o NEW (mantido); o duplicado a ser desativado é o OLD.
  const [newFreelancer, setNewFreelancer] = useState<FreelancerLite>(currentFreelancer);
  const [oldId, setOldId] = useState<string>("");
  const [oldFreelancer, setOldFreelancer] = useState<FreelancerLite | null>(null);

  // Reset quando o modal abre/fecha
  useEffect(() => {
    if (open) {
      setStep(1);
      setNewFreelancer(currentFreelancer);
      setOldId("");
      setOldFreelancer(null);
    }
  }, [open, currentFreelancer]);

  const handleOldChange = (id: string, opt?: FreelancerOption) => {
    setOldId(id);
    setOldFreelancer(opt ? { id: opt.id, nome: opt.nome, email: opt.email } : null);
  };

  const handleSwapSides = () => {
    if (!oldFreelancer) return;
    const previousNew = newFreelancer;
    setNewFreelancer(oldFreelancer);
    setOldId(previousNew.id);
    setOldFreelancer(previousNew);
  };

  const canAdvanceFromStep1 = !!oldId && oldId !== newFreelancer.id;

  // Step 2: preview
  const preview = useQuery<MergePreview>({
    queryKey: ["freelancer-merge-preview", oldId, newFreelancer.id],
    queryFn: () => previewMerge(oldId, newFreelancer.id),
    enabled: open && step === 2 && !!oldId,
    staleTime: 0,
  });

  // Step 3: executa
  const merge = useMutation({
    mutationFn: () => executeMerge(oldId, newFreelancer.id),
    onSuccess: () => {
      invalidateFreelancers();
      queryClient.invalidateQueries({ queryKey: ["freelancer", newFreelancer.id] });
      queryClient.invalidateQueries({ queryKey: ["freelancer", oldId] });
      queryClient.invalidateQueries({ queryKey: ["freelancers"] });
    },
  });

  const handleConfirmFinish = () => {
    onOpenChange(false);
    // Se o usuário trocou lados (atual virou OLD), redireciona para o NEW
    if (newFreelancer.id !== currentFreelancer.id) {
      navigate(`/freelancers/${newFreelancer.id}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[760px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Merge className="h-5 w-5" />
            Mesclar Freelancers
          </DialogTitle>
          <DialogDescription>
            Passo {step} de 3: {step === 1 ? "Selecionar duplicado" : step === 2 ? "Revisar mesclagem" : "Confirmação"}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <Step1Selection
            newFreelancer={newFreelancer}
            oldFreelancer={oldFreelancer}
            oldId={oldId}
            onOldChange={handleOldChange}
            onSwapSides={handleSwapSides}
          />
        )}

        {step === 2 && (
          <Step2Review
            preview={preview.data}
            isLoading={preview.isLoading}
            error={preview.error as Error | null}
          />
        )}

        {step === 3 && (
          <Step3Execute
            mergeStatus={merge.status}
            error={merge.error as Error | null}
            counts={merge.data?.counts}
            mergeId={merge.data?.merge_id}
            newFreelancer={newFreelancer}
            oldFreelancer={oldFreelancer}
          />
        )}

        <DialogFooter>
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button
                disabled={!canAdvanceFromStep1}
                onClick={() => setStep(2)}
              >
                Próximo
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>
                <ChevronLeft className="mr-1 h-4 w-4" />
                Voltar
              </Button>
              <Button
                variant="destructive"
                disabled={preview.isLoading || !!preview.error}
                onClick={() => {
                  setStep(3);
                  merge.mutate();
                }}
              >
                Confirmar Mesclagem
              </Button>
            </>
          )}

          {step === 3 && (
            <>
              {merge.status === "error" && (
                <Button variant="outline" onClick={() => setStep(2)}>
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Voltar
                </Button>
              )}
              <Button
                disabled={merge.status === "pending"}
                onClick={handleConfirmFinish}
              >
                {merge.status === "success" ? "Concluir" : "Fechar"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================
// Step 1 — Seleção
// =============================================================
interface Step1Props {
  newFreelancer: FreelancerLite;
  oldFreelancer: FreelancerLite | null;
  oldId: string;
  onOldChange: (id: string, opt?: FreelancerOption) => void;
  onSwapSides: () => void;
}

function Step1Selection({
  newFreelancer,
  oldFreelancer,
  oldId,
  onOldChange,
  onSwapSides,
}: Step1Props) {
  return (
    <div className="space-y-4 py-2">
      <p className="text-sm text-muted-foreground">
        Escolha o perfil duplicado que será desativado. Todas as referências (projetos, vagas, avaliações, kanban etc.) serão movidas para o perfil mantido.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
        <SideCard
          tone="keep"
          title="Será mantido"
          freelancer={newFreelancer}
        />

        <div className="flex md:flex-col items-center justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={onSwapSides}
            disabled={!oldFreelancer}
            title="Trocar lados"
          >
            <ArrowLeftRight className="h-4 w-4" />
            <span className="ml-1 md:sr-only">Trocar</span>
          </Button>
        </div>

        <SideCard
          tone="discard"
          title="Será desativado"
          freelancer={oldFreelancer}
        >
          <div className="mt-2">
            <FreelancerAutocomplete
              value={oldId}
              onValueChange={onOldChange}
              selectedLabel={oldFreelancer?.nome}
              placeholder="Selecione o duplicado..."
            />
          </div>
        </SideCard>
      </div>

      {oldFreelancer && oldFreelancer.id === newFreelancer.id && (
        <Alert variant="destructive">
          <TriangleAlert className="h-4 w-4" />
          <AlertDescription>
            O perfil mantido e o desativado não podem ser o mesmo.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

interface SideCardProps {
  tone: "keep" | "discard";
  title: string;
  freelancer: FreelancerLite | null;
  children?: React.ReactNode;
}

function SideCard({ tone, title, freelancer, children }: SideCardProps) {
  const ringClass =
    tone === "keep"
      ? "border-emerald-300 bg-emerald-50/50"
      : "border-rose-300 bg-rose-50/50";
  return (
    <Card className={`${ringClass} border-2`}>
      <CardContent className="p-4">
        <Badge variant={tone === "keep" ? "default" : "destructive"} className="mb-2">
          {title}
        </Badge>
        {freelancer ? (
          <>
            <div className="font-medium leading-tight">{freelancer.nome}</div>
            <div className="text-xs text-muted-foreground truncate">{freelancer.email}</div>
          </>
        ) : (
          <div className="text-sm text-muted-foreground italic">
            Nenhum selecionado
          </div>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

// =============================================================
// Step 2 — Revisão
// =============================================================
interface Step2Props {
  preview: MergePreview | undefined;
  isLoading: boolean;
  error: Error | null;
}

function Step2Review({ preview, isLoading, error }: Step2Props) {
  if (isLoading) {
    return (
      <div className="space-y-3 py-2">
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <TriangleAlert className="h-4 w-4" />
        <AlertTitle>Erro ao gerar prévia</AlertTitle>
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    );
  }

  if (!preview) return null;

  const itemsToMove = Object.entries(preview.counts).filter(([, n]) => n > 0);
  const conflictItems = Object.entries(preview.conflicts ?? {}).filter(([, n]) => n > 0);

  return (
    <div className="space-y-4 py-2">
      <div className="text-sm">
        <span className="font-medium">{preview.old.nome}</span>
        <span className="text-muted-foreground"> ({preview.old.email})</span>
        <span className="mx-2 text-muted-foreground">→</span>
        <span className="font-medium">{preview.new.nome}</span>
        <span className="text-muted-foreground"> ({preview.new.email})</span>
      </div>

      <div>
        <h3 className="text-sm font-semibold mb-2">Diferenças nos campos do perfil</h3>
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-3 py-2 font-medium w-[24%]">Campo</th>
                <th className="text-left px-3 py-2 font-medium">Desativado</th>
                <th className="text-left px-3 py-2 font-medium">Mantido</th>
              </tr>
            </thead>
            <tbody>
              {DIFF_FIELDS.map(({ key, label }) => {
                const oldVal = preview.old[key];
                const newVal = preview.new[key];
                const diff = isDifferent(oldVal, newVal);
                return (
                  <tr
                    key={key as string}
                    className={diff ? "bg-amber-50" : ""}
                  >
                    <td className="px-3 py-2 align-top text-muted-foreground">{label}</td>
                    <td className="px-3 py-2 align-top wrap-break-word">{formatValue(oldVal)}</td>
                    <td className="px-3 py-2 align-top wrap-break-word">{formatValue(newVal)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Linhas em amarelo indicam divergência. O perfil mantido fica como está; campos do perfil não são copiados automaticamente.
        </p>
      </div>

      <Separator />

      <div>
        <h3 className="text-sm font-semibold mb-2">Será movido para o perfil mantido</h3>
        {itemsToMove.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nada para mover: o perfil desativado não tem dados associados.</p>
        ) : (
          <ul className="text-sm space-y-1">
            {itemsToMove.map(([key, n]) => (
              <li key={key} className="flex items-center gap-2">
                <Badge variant="secondary" className="font-mono">{n}</Badge>
                <span>{labelForFkKey(key)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {conflictItems.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">Conflitos resolvidos automaticamente</h3>
          <ul className="text-sm space-y-1">
            {conflictItems.map(([key, n]) => (
              <li key={key} className="flex items-center gap-2 text-muted-foreground">
                <Badge variant="outline" className="font-mono">{n}</Badge>
                <span>{labelForFkKey(key)}: duplicado descartado (perfil mantido já tem)</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Alert>
        <TriangleAlert className="h-4 w-4" />
        <AlertTitle>Esta ação é definitiva pelo wizard</AlertTitle>
        <AlertDescription>
          O perfil desativado será marcado como inativo. Para reverter, consulte a tabela{" "}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">freelancer_merges</code>.
        </AlertDescription>
      </Alert>
    </div>
  );
}

// =============================================================
// Step 3 — Execução
// =============================================================
interface Step3Props {
  mergeStatus: "idle" | "pending" | "success" | "error";
  error: Error | null;
  counts?: Record<string, number>;
  mergeId?: string;
  newFreelancer: FreelancerLite;
  oldFreelancer: FreelancerLite | null;
}

function Step3Execute({
  mergeStatus,
  error,
  counts,
  mergeId,
  newFreelancer,
  oldFreelancer,
}: Step3Props) {
  if (mergeStatus === "pending" || mergeStatus === "idle") {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-3">
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Mesclando perfis...</p>
      </div>
    );
  }

  if (mergeStatus === "error") {
    return (
      <Alert variant="destructive">
        <TriangleAlert className="h-4 w-4" />
        <AlertTitle>Falha ao mesclar</AlertTitle>
        <AlertDescription>{error?.message ?? "Erro desconhecido"}</AlertDescription>
      </Alert>
    );
  }

  const moved = Object.entries(counts ?? {}).filter(([, n]) => n > 0);

  return (
    <div className="space-y-4 py-2">
      <div className="flex items-center gap-2 text-emerald-700">
        <CheckCircle2 className="h-6 w-6" />
        <span className="font-semibold">Mesclagem concluída</span>
      </div>

      <p className="text-sm">
        {oldFreelancer?.nome ?? "Perfil desativado"} foi desativado. Todas as referências agora apontam para{" "}
        <span className="font-medium">{newFreelancer.nome}</span>.
      </p>

      {moved.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">Movido</h3>
          <ul className="text-sm space-y-1">
            {moved.map(([key, n]) => (
              <li key={key} className="flex items-center gap-2">
                <Badge variant="secondary" className="font-mono">{n}</Badge>
                <span>{labelForFkKey(key)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {mergeId && (
        <p className="text-xs text-muted-foreground">
          Auditoria registrada: <code className="bg-muted px-1 py-0.5 rounded">{mergeId}</code>
        </p>
      )}
    </div>
  );
}
